import React, { useState } from 'react';
import { Box, Typography, CircularProgress, alpha } from '@mui/material';
import { LOJ_COLORS } from '../theme/lojTheme';
import { useRoster, formatEventDateTime, RosterLegion, RosterSquad } from '../hooks/useRoster';
import OrnamentalDivider from './OrnamentalDivider';
import { useTranslation } from '../i18n/LanguageContext';

// ─── Role styling ─────────────────────────────────────────────────────────────

const ROLE_STYLE = {
  Commander: {
    bg:     alpha(LOJ_COLORS.goldDim, 0.45),
    border: LOJ_COLORS.goldBright,
    text:   LOJ_COLORS.goldGlow,
    icon:   '👑',
  },
  Warrior: {
    bg:     alpha(LOJ_COLORS.bloodDark, 0.55),
    border: LOJ_COLORS.ember,
    text:   LOJ_COLORS.ember,
    icon:   '⚔️',
  },
  Engineer: {
    bg:     alpha(LOJ_COLORS.crystalDeep, 0.7),
    border: LOJ_COLORS.crystalLight,
    text:   LOJ_COLORS.crystalLight,
    icon:   '⚙️',
  },
};

// ─── Member row ───────────────────────────────────────────────────────────────

const MemberRow: React.FC<{ name: string; role: keyof typeof ROLE_STYLE }> = ({ name, role }) => {
  const { t } = useTranslation();
  const s = ROLE_STYLE[role] ?? ROLE_STYLE.Warrior;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 0.8,
        px: 1.5,
        borderBottom: `1px solid ${alpha(s.border, 0.1)}`,
        transition: 'background 0.15s',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { background: alpha(s.border, 0.06) },
      }}
    >
      {/* Role badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          flexShrink: 0,
          px: 1,
          py: 0.2,
          background: s.bg,
          border: `1px solid ${alpha(s.border, 0.45)}`,
          minWidth: 100,
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1 }}>{s.icon}</Typography>
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontSize: '0.62rem',
            letterSpacing: '0.1em',
            color: s.text,
            fontWeight: 700,
          }}
        >
          {t(role).toUpperCase()}
        </Typography>
      </Box>

      {/* Name */}
      <Typography
        sx={{
          fontFamily: "'Crimson Text', Georgia, serif",
          fontSize: '1.05rem',
          color: LOJ_COLORS.parchmentLight,
          lineHeight: 1.4,
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

// ─── Squad card ───────────────────────────────────────────────────────────────

const SQUAD_ACCENT = [
  LOJ_COLORS.crystalLight,
  LOJ_COLORS.goldBright,
  LOJ_COLORS.ember,
  LOJ_COLORS.sandLight,
  LOJ_COLORS.crystalGlow,
  LOJ_COLORS.parchment,
];

const isHitSquad = (n: number) => n === 6;

const SquadCard: React.FC<{ squad: RosterSquad }> = ({ squad }) => {
  const { t } = useTranslation();
  const accent = SQUAD_ACCENT[(squad.squadNumber - 1) % SQUAD_ACCENT.length];
  const commander = squad.members.find(m => m.role === 'Commander');
  const others    = squad.members.filter(m => m.role !== 'Commander');

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.95)}, ${alpha(LOJ_COLORS.stone, 0.85)})`,
        border: `1px solid ${alpha(accent, 0.3)}`,
        borderTop: `2px solid ${accent}`,
        boxShadow: `0 4px 20px ${alpha(LOJ_COLORS.void, 0.7)}, 0 0 0 0 transparent`,
        transition: 'box-shadow 0.25s ease',
        '&:hover': {
          boxShadow: `0 6px 28px ${alpha(LOJ_COLORS.void, 0.8)}, 0 0 16px ${alpha(accent, 0.12)}`,
        },
      }}
    >
      {/* Squad header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.2,
          borderBottom: `1px solid ${alpha(accent, 0.2)}`,
          background: alpha(accent, 0.07),
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 700,
            fontSize: '0.85rem',
            letterSpacing: '0.12em',
            color: accent,
            textShadow: `0 0 12px ${alpha(accent, 0.4)}`,
          }}
        >
          {squad.squadNumber <= 3 ? `${t('SQUAD')} ${String.fromCharCode(64 + squad.squadNumber)}` : t('Independent Roaming Support')}
        </Typography>
        {squad.squadNumber === 6 && isHitSquad(squad.squadNumber) && (
          <Box
            sx={{
              px: 1,
              py: 0.15,
              background: alpha(LOJ_COLORS.ember, 0.25),
              border: `1px solid ${alpha(LOJ_COLORS.ember, 0.5)}`,
              fontFamily: "'Cinzel', serif",
              fontSize: '0.58rem',
              letterSpacing: '0.1em',
              color: LOJ_COLORS.ember,
              fontWeight: 700,
            }}
          >
            {t('HIT SQUAD')}
          </Box>
        )}
        <Box sx={{ ml: 'auto', fontFamily: "'Cinzel', serif", fontSize: '0.65rem', color: alpha(accent, 0.6), letterSpacing: '0.08em' }}>
          {squad.members.length} {t('MEMBERS')}
        </Box>
      </Box>

      {/* Members */}
      <Box>
        {commander && <MemberRow name={commander.name} role="Commander" />}
        {others.map((m, i) => (
          <MemberRow key={i} name={m.name} role={m.role as keyof typeof ROLE_STYLE} />
        ))}
      </Box>
    </Box>
  );
};

// ─── Legion panel ─────────────────────────────────────────────────────────────

const LegionPanel: React.FC<{ legion: RosterLegion; isActive: boolean }> = ({ legion, isActive }) => {
  const { t } = useTranslation();
  const totalMembers = legion.squads.reduce((sum, s) => sum + s.members.length, 0);

  return (
    <Box
      sx={{
        display: isActive ? 'block' : 'none',
        animation: isActive ? 'fadeIn 0.35s ease' : 'none',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to:   { opacity: 1, transform: 'translateY(0)'   },
        },
      }}
    >
      {/* Event time banner */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.95)}, ${alpha(LOJ_COLORS.stone, 0.8)})`,
          border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.3)}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: '1.3rem' }}>📅</Typography>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Cinzel', serif",
              fontSize: '0.7rem',
              letterSpacing: '0.15em',
              color: LOJ_COLORS.goldMuted,
              mb: 0.3,
            }}
          >
            {t('EVENT TIME')}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Cinzel', serif",
              fontSize: { xs: '0.9rem', md: '1rem' },
              fontWeight: 600,
              color: LOJ_COLORS.goldBright,
              letterSpacing: '0.05em',
            }}
          >
            {formatEventDateTime(legion.date, legion.time)}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
          <Typography
            sx={{
              fontFamily: "'Cinzel', serif",
              fontSize: '0.65rem',
              letterSpacing: '0.12em',
              color: LOJ_COLORS.parchmentDark,
            }}
          >
            {legion.squads.length} {t('SQUADS')} · {totalMembers} {t('MEMBERS')}
          </Typography>
        </Box>
      </Box>

      {/* Squad grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {legion.squads.map(squad => (
          <SquadCard key={squad.squadNumber} squad={squad} />
        ))}
      </Box>
    </Box>
  );
};

// ─── Main Roster component ────────────────────────────────────────────────────

const CrystalClashRoster: React.FC = () => {
  const { t } = useTranslation();
  const [activeLegion, setActiveLegion] = useState(0);
  const { legions, loading, error, lastUpdated } = useRoster();

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, mb: 4 }}>
      <OrnamentalDivider label={t('Event Roster')} color={LOJ_COLORS.crystalLight} />

      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontSize: '1.1rem', color: LOJ_COLORS.crystalLight, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <span>🔮</span> {t('Crystal Clash — Legion Assignments')}
        </Typography>
        {lastUpdated && (
          <Typography sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.65rem', letterSpacing: '0.1em', color: LOJ_COLORS.goldDim }}>
            {t('LOADED')} {lastUpdated}
          </Typography>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={20} sx={{ color: LOJ_COLORS.crystalLight }} />
          <Typography sx={{ fontFamily: "'Cinzel', serif", fontSize: '0.8rem', color: LOJ_COLORS.parchmentDark, letterSpacing: '0.1em' }}>
            {t('LOADING ROSTER...')}
          </Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Box
          sx={{
            p: 2.5,
            background: alpha(LOJ_COLORS.bloodDark, 0.5),
            border: `1px solid ${alpha(LOJ_COLORS.ember, 0.4)}`,
            borderLeft: `3px solid ${LOJ_COLORS.ember}`,
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Typography sx={{ color: LOJ_COLORS.ember }}>⚠</Typography>
          <Typography sx={{ color: alpha(LOJ_COLORS.ember, 0.9), fontFamily: "'Crimson Text', Georgia, serif", fontSize: '1rem' }}>
            {t(error)}
          </Typography>
        </Box>
      )}

      {/* Content */}
      {!loading && !error && legions.length > 0 && (
        <>
          {/* Legion tab switcher */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {legions.map((legion, i) => (
              <Box
                key={legion.legion}
                onClick={() => setActiveLegion(i)}
                sx={{
                  flex: '1 1 160px',
                  cursor: 'pointer',
                  p: 2,
                  textAlign: 'center',
                  background: activeLegion === i
                    ? `linear-gradient(135deg, ${alpha(LOJ_COLORS.crystalLight, 0.18)}, ${alpha(LOJ_COLORS.crystalLight, 0.07)})`
                    : `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.9)}, ${alpha(LOJ_COLORS.stone, 0.7)})`,
                  border: `1px solid ${activeLegion === i ? alpha(LOJ_COLORS.crystalLight, 0.7) : alpha(LOJ_COLORS.crystalLight, 0.2)}`,
                  borderTop: activeLegion === i ? `3px solid ${LOJ_COLORS.crystalLight}` : `1px solid ${alpha(LOJ_COLORS.crystalLight, 0.2)}`,
                  boxShadow: activeLegion === i ? `0 0 20px ${alpha(LOJ_COLORS.crystalGlow, 0.18)}` : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(LOJ_COLORS.crystalLight, activeLegion === i ? 0.7 : 0.4),
                    background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.crystalLight, activeLegion === i ? 0.18 : 0.08)}, ${alpha(LOJ_COLORS.crystalLight, 0.04)})`,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Cinzel', serif",
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.1em',
                    color: activeLegion === i ? LOJ_COLORS.crystalLight : LOJ_COLORS.parchment,
                    textShadow: activeLegion === i ? `0 0 12px ${alpha(LOJ_COLORS.crystalGlow, 0.5)}` : 'none',
                    mb: 0.5,
                  }}
                >
                  {t('Legion')} {legion.legion}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Crimson Text', Georgia, serif",
                    fontSize: '0.88rem',
                    color: activeLegion === i ? alpha(LOJ_COLORS.crystalLight, 0.8) : LOJ_COLORS.parchmentDark,
                    fontStyle: 'italic',
                  }}
                >
                  {legion.time} ST
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Legion content */}
          {legions.map((legion, i) => (
            <LegionPanel key={legion.legion} legion={legion} isActive={activeLegion === i} />
          ))}
        </>
      )}
    </Box>
  );
};

export default CrystalClashRoster;
